<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-user ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('benutzer') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="isSaving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        <span>{{ headerFirstPart }}</span>
                        <span v-if="headerSecondPart" class="record-title">{{ headerSecondPart }}</span>
                    </v-toolbar-title>
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <ActionBtn :disabled="!isValid" :loading="isSaving" color="primary" @click.stop="save">
                            <span><KpIcon icon-cls="save" color="white" /> {{ $t('save') }}</span>
                        </ActionBtn>
                        <ActionBtn color="primary" @click.stop="cancel()">
                            <span><KpIcon icon-cls="x" color="white" /> {{ $t('schliessen') }}</span>
                        </ActionBtn>
                        <v-dialog v-model="showRelationDialog" scrollable max-width="1264px">
                            <template #activator="{ on, attrs }">
                                <ActionBtn color="primary" v-bind="attrs" v-on="on">
                                    <span><KpIcon icon-cls="link" color="white" /> {{ $t('show_relationen') }}</span>
                                </ActionBtn>
                            </template>
                            <v-card>
                                <v-toolbar flat>
                                    <v-spacer />
                                    <v-btn @click="showRelationDialog = false">{{ $t('schliessen') }}</v-btn>
                                </v-toolbar>
                                <v-divider />

                                <v-card-text style="height: 500px;">
                                    <UserRelations :record="record" />
                                </v-card-text>

                                <v-divider />
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn @click="showRelationDialog = false">{{ $t('schliessen') }}</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </v-slide-group>
                </v-toolbar>
            </div>

            <!-- Main content -->
            <v-container fluid class="content overflow-auto pa-5 ma-0">
                <v-form ref="form" style="max-width: 900px" @input="isValid = $event">
                    <v-row dense>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <h4>{{ $t('name_contact') }}</h4>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('name')">
                                <v-text-field
                                    hide-details="auto"
                                    :value="record?.lastname"
                                    counter="100"
                                    :rules="getRules('required')"
                                    dense
                                    outlined
                                    @input="onInput('lastname', $event)"
                                />
                            </LabelField>
                        </v-col>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('vorname')">
                                <v-text-field
                                    hide-details="auto"
                                    :value="record?.firstname"
                                    counter="100"
                                    :rules="getRules('required')"
                                    dense
                                    outlined
                                    @input="onInput('firstname', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <div class="d-flex align-start" style="gap: 20px">
                                <LabelField :label="$t('role_name')">
                                    {{ $t(record?.role.toLowerCase()) }}
                                </LabelField>
                                <LabelField :label="$t('organizer')">
                                    {{ record?.title }}
                                </LabelField>
                            </div>
                        </v-col>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('email')">
                                <v-text-field
                                    hide-details="auto"
                                    :value="record?.email"
                                    counter="255"
                                    :rules="getRules('required')"
                                    dense
                                    outlined
                                    @input="onInput('email', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <h4>{{ $t('login') }}</h4>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('username')">
                                {{ record?.username }}
                            </LabelField>
                        </v-col>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('id')">
                                {{ record?.id }}
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('new_pw')">
                                <v-text-field
                                    hide-details="auto"
                                    :value="record?.newPw"
                                    type="password"
                                    :rules="getPWFieldRules()"
                                    dense
                                    outlined
                                    @input="
                                        onInput('newPw', $event);
                                        validateForm();
                                    "
                                />
                            </LabelField>
                        </v-col>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('confirm_pw')">
                                <v-text-field
                                    hide-details="auto"
                                    :value="record?.newPwConfirm"
                                    :rules="getPWConfirmFieldRules()"
                                    type="password"
                                    dense
                                    outlined
                                    @input="onInput('newPwConfirm', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <v-row class="mt-6" dense>
                                <v-col cols="12">
                                    <h4>{{ $t('infomail') }}</h4>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <v-col cols="12">
                                    <v-checkbox
                                        class="mt-0 pt-0"
                                        :input-value="record?.infomail_tgk"
                                        :label="$t('infomails_erhalten')"
                                        hide-details="auto"
                                        @change="onInput('infomail_tgk', $event)"
                                    />
                                    <div v-if="record?.infomail_tgk_date.isValid()" class="info-text">
                                        {{ $t('zusagedatum_infomail') }}:
                                        {{ record?.infomail_tgk_date.formatIf('dd, DD.MM.YYYY') }}
                                    </div>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <v-row class="mt-6" dense>
                                <v-col cols="12">
                                    <h4>{{ $t('kulturverbund') }}</h4>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <v-col cols="12">
                                    <LabelField :label="$t('kulturverbund')">
                                        {{ record?.organizer_group }}
                                    </LabelField>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row
                        v-bind="{
                            dense: !$vuetify.breakpoint.mdAndDown,
                            noGutters: $vuetify.breakpoint.mdAndDown,
                        }"
                    >
                        <v-col cols="12" lg="6" :class="{ 'py-1': $vuetify.breakpoint.mdAndDown }">
                            <LabelField :label="$t('sprache')">
                                <LangSelect :value="record?.locale || 'de'" @input="onInput('locale', $event)" />
                            </LabelField>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-card>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import getRulesMixin from '@/mixins/get-form-rules';
import { errToStr, setBrowserTitle } from '@/helpers/tools';
import { hex_sha1 } from '@appvise/digest-sha1';
import LangSelect from '@/components/LangSelect.vue';
import UserRelations from '@/views/auth/user/_id/Relations.vue';

export default {
    name: 'UserProfileEdit',
    components: {
        LangSelect,
        UserRelations,
    },
    mixins: [getRulesMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.save();
        if (to.params.id !== from.params.id) {
            this.record = this.user;
            this.validateForm();
        }
        next();
    },

    data() {
        return {
            record: null,
            isValid: false,
            isSaving: false,
            showRelationDialog: false,
        };
    },

    computed: {
        headerFirstPart() {
            return `${this.$t('user_profile')}${this.headerSecondPart ? `: ` : ''}`;
        },
        headerSecondPart() {
            return this.record?.fullname;
        },
        browserTitle() {
            if (this.headerSecondPart) {
                return `${this.$t('user_profile')} - ${this.headerSecondPart}`;
            } else {
                return this.headerFirstPart;
            }
        },
        ...mapState('user', {
            user: 'record',
        }),
    },

    watch: {
        browserTitle: {
            handler() {
                setBrowserTitle(this.getBrowserTitle());
            },
            immediate: true,
        },
    },

    mounted() {
        this.record = this.user;
        this.validateForm();
    },

    methods: {
        ...mapActions(['runAsync']),

        validateForm() {
            this.$refs.form?.validate();
        },

        getPWFieldRules() {
            return [this.getRule('minLengthOptional')(8)];
        },

        getPWConfirmFieldRules() {
            return [() => this.record?.newPw === this.record?.newPwConfirm || this.$t('pw_not_match')];
        },

        onInput(field, value) {
            this.record.set(field, value);
        },

        async save() {
            if (!this.record) {
                return false;
            }
            if (!this.isValid) {
                return false;
            }

            try {
                this.isSaving = true;

                if (this.record?.newPw) {
                    this.record.password_change = hex_sha1(this.record?.newPw);
                }

                if (this.record?.hasUnsavedData()) {
                    await this.runAsync(async () => await this.record?.saveProfile());
                }

                return true;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.isSaving = false;
            }
        },

        cancel() {
            if (window.history.length > 2) {
                this.$router.back();
            } else {
                this.$router.push({ name: 'home' });
            }
        },

        getBrowserTitle() {
            return this.browserTitle;
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-areas:
        'header'
        'content';
}

.top-header {
    grid-area: header;
}

.content {
    grid-area: content;
    padding: 30px;
}

.info-text {
    font-size: 14px;
    text-indent: 32px;
    opacity: 0.36;
}
</style>
